import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  ADD_NOTE_SHORTCUT,
  BILLING_QUEUE_PATH,
  BILLING_TAB_SHORTCUTS,
  FIND_PATIENT_SHORTCUT,
  HELP_SHORTCUT,
  NAV_SHORTCUTS,
  SEARCH_RX_SHORTCUT,
  TASK_QUEUE_SHORTCUT,
  WIZARD_KEY_HINTS,
  formatShortcut,
} from '../../constants/keyboardShortcuts';
import { useShiftShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useVisibleMenuItems, visibleMenuPaths } from '../Layout/NewSidebar';
import { useGlobalPatientSearch } from '../PatientSearch/GlobalPatientSearchProvider';
import { useTaskQueueStore } from '../../stores/taskQueueStore';

/**
 * GP-482 — app-wide keyboard shortcuts, mounted once in `MainLayout`.
 *
 * Binds the ribbon buttons and the queues down the sidebar to `Shift + <letter>`,
 * plus `Shift + ?` for the cheat sheet. Page-scoped keys live with their page:
 * the Billing Queue owns `Shift + 1..6`, `QueueView` owns `Shift + Q`, the
 * Process Billing wizard owns Enter. Every one of them reads its key from
 * `constants/keyboardShortcuts.ts`.
 *
 * A destination the user can't see in the sidebar isn't reachable by hotkey
 * either. PRX-138: the gate IS the sidebar's filter (`useVisibleMenuItems`):
 * permission, roles, env flag, nurse scope, pharmacy modules and PRX-54
 * visibility keys, with its graceful fallback (no custom role → everything
 * shows). The help dialog lists the same set.
 */
export const AppShortcuts: React.FC = () => {
  const navigate = useNavigate();
  const visibleMenuItems = useVisibleMenuItems();
  const { openPatientSearch } = useGlobalPatientSearch();
  const openTaskQueue = useTaskQueueStore((state) => state.open);
  const [helpOpen, setHelpOpen] = useState(false);

  const visiblePaths = useMemo(() => visibleMenuPaths(visibleMenuItems), [visibleMenuItems]);
  const visibleNavShortcuts = useMemo(
    () => NAV_SHORTCUTS.filter((shortcut) => visiblePaths.has(shortcut.path)),
    [visiblePaths],
  );
  // The Billing Queue owns Shift + 1..6 and the Process Billing wizard; their
  // keys only help a user who can open it.
  const billingQueueVisible = visiblePaths.has(BILLING_QUEUE_PATH);

  const handlers = useMemo(() => {
    const map: Record<string, () => void> = {
      [FIND_PATIENT_SHORTCUT.key]: () => openPatientSearch(),
      [TASK_QUEUE_SHORTCUT.key]: () => openTaskQueue(),
      [HELP_SHORTCUT.key]: () => setHelpOpen((prev) => !prev),
    };
    visibleNavShortcuts.forEach((shortcut) => {
      map[shortcut.key] = () => navigate(shortcut.path);
    });
    return map;
  }, [navigate, openPatientSearch, openTaskQueue, visibleNavShortcuts]);

  useShiftShortcuts(handlers);

  return (
    <KeyboardShortcutsDialog
      open={helpOpen}
      onClose={() => setHelpOpen(false)}
      navShortcuts={visibleNavShortcuts.map((s) => ({ combo: formatShortcut(s.key), label: s.label }))}
      showBillingTabs={billingQueueVisible}
    />
  );
};

interface ShortcutRow {
  combo: string;
  label: string;
}

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
  navShortcuts: ShortcutRow[];
  showBillingTabs: boolean;
}

/** The `Shift + ?` cheat sheet — the only place staff have to look. */
const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
  navShortcuts,
  showBillingTabs,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1 }}>Keyboard shortcuts</Box>
      <IconButton aria-label="close" onClick={onClose} size="small">
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Shortcuts stay out of the way while you type. They only fire when the cursor isn't in a
        field, and never on top of an open dialog or menu. Queues you can't open are left out.
      </Typography>

      <ShortcutSection title="Go to a queue" rows={navShortcuts} />
      <ShortcutSection
        title="Open a panel"
        rows={[
          { combo: formatShortcut(FIND_PATIENT_SHORTCUT.key), label: FIND_PATIENT_SHORTCUT.label },
          { combo: formatShortcut(TASK_QUEUE_SHORTCUT.key), label: TASK_QUEUE_SHORTCUT.label },
          { combo: formatShortcut(SEARCH_RX_SHORTCUT.key), label: SEARCH_RX_SHORTCUT.label },
          { combo: formatShortcut(ADD_NOTE_SHORTCUT.key), label: ADD_NOTE_SHORTCUT.label },
          { combo: 'Shift + ?', label: HELP_SHORTCUT.label },
        ]}
        last={!showBillingTabs}
      />
      {showBillingTabs && (
        <>
          <ShortcutSection
            title="Billing queue tabs"
            rows={BILLING_TAB_SHORTCUTS.map((tab) => ({
              combo: `Shift + ${tab.key}`,
              label: tab.label,
            }))}
          />
          <ShortcutSection title="Process billing" rows={WIZARD_KEY_HINTS} last />
        </>
      )}
    </DialogContent>
  </Dialog>
);

const ShortcutSection: React.FC<{ title: string; rows: ShortcutRow[]; last?: boolean }> = ({
  title,
  rows,
  last = false,
}) => (
  <Box sx={{ mb: last ? 0 : 2 }}>
    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {rows.map((row) => (
        <Stack key={`${title}-${row.combo}`} direction="row" spacing={1.5} alignItems="center">
          <Chip
            label={row.combo}
            size="small"
            variant="outlined"
            sx={{ fontFamily: 'monospace', minWidth: 132, justifyContent: 'flex-start' }}
          />
          <Typography variant="body2">{row.label}</Typography>
        </Stack>
      ))}
    </Stack>
    {!last && <Divider sx={{ mt: 2 }} />}
  </Box>
);

export default AppShortcuts;
